import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { getFaultName } from '@feedflo/fault-codes';
import LoadingSkeleton from '../../atoms/LoadingSkeleton';
import Button from '../../atoms/Button';
import NumberInput from '../../atoms/NumberInput';
import ToggleSwitch from '../../atoms/ToggleSwitch';
import Select from '../../atoms/Select';
import Checkbox from '../../atoms/Checkbox';
import './EventFiltersCard.scss';

const EventFiltersCard = forwardRef(function EventFiltersCard(
  {
    className = '',
    onClose = () => {},
    currentEventFilters = [],
    loading = false,
    title = 'Event Filters',
    onReset = () => {},
    onEventFiltersChange = () => {},
    rootCauseList = [],
  },
  ref,
) {
  const handleClose = () => {
    onClose();
  };

  const safeGetFaultName = (code) => {
    try {
      return getFaultName(code);
    } catch (error) {
      return `Fault Code ${code}`;
    }
  };

  // Time type options
  const timeTypeOptions = [
    { id: 'duration', value: 'duration', name: 'after event began' },
    { id: 'before_empty', value: 'before_empty', name: 'before feeders are empty' },
    { id: 'after_empty', value: 'after_empty', name: 'after feeders are empty' },
  ];

  // Handle filter changes and apply immediately
  const handleFaultFilterChange = (faultCode, field, value) => {
    // Create new settings based on current filterSettings
    const newSettings = {
      ...currentEventFilters,
      faults: {
        ...currentEventFilters.faults,
        [faultCode]: {
          ...currentEventFilters.faults[faultCode],
          [field]: value,
        },
      },
    };

    newSettings.faults[faultCode].filter_threshold_in_seconds = Math.abs(
      newSettings.faults[faultCode].filter_threshold_in_seconds,
    );

    if (newSettings.faults[faultCode].filter_type === 'before_empty') {
      newSettings.faults[faultCode].filter_threshold_in_seconds *= -1;
    }

    onEventFiltersChange(newSettings);
  };

  const handleRootCauseFilterChange = (rootCause) => {
    const isSelected = currentEventFilters.selected_root_causes.some(
      (selected) => selected.id === rootCause.id || selected.name === rootCause.name,
    );

    let newSelectedRootCauses;
    if (isSelected) {
      // Remove the root cause
      newSelectedRootCauses = currentEventFilters.selected_root_causes.filter(
        (selected) => selected.id !== rootCause.id && selected.name !== rootCause.name,
      );
    } else {
      // Add the root cause
      newSelectedRootCauses = [...currentEventFilters.selected_root_causes, rootCause];
    }

    onEventFiltersChange({
      ...currentEventFilters,
      selected_root_causes: newSelectedRootCauses,
    });
  };

  const handleReset = () => {
    onReset();
  };

  if (loading) {
    return (
      <div className={`EventFiltersCard ${className}`} ref={ref}>
        <div className="EventFiltersCard-header">
          <div className="EventFiltersCard-title">
            <img src="/icons/filter.png" alt="Filter" className="EventFiltersCard-titleIcon" />
            {title}
          </div>
        </div>
        <div className="EventFiltersCard-loading">
          <LoadingSkeleton />
          <LoadingSkeleton />
        </div>
      </div>
    );
  }

  if (Object.keys(currentEventFilters.faults).length === 0) {
    return (
      <div className={`EventFiltersCard ${className}`} ref={ref}>
        <div className="EventFiltersCard-header">
          <div className="EventFiltersCard-title">
            <img src="/icons/filter.png" alt="Filter" className="EventFiltersCard-titleIcon" />
            {title}
          </div>
        </div>
        <div className="EventFiltersCard-empty">No event filters available</div>
      </div>
    );
  }

  return (
    <div className={`EventFiltersCard ${className}`} ref={ref}>
      <div className="EventFiltersCard-header">
        <div className="EventFiltersCard-title">
          <img src="/icons/filter.png" alt="Filter" className="EventFiltersCard-titleIcon" />
          {title}
        </div>
      </div>
      <div className="EventFiltersCard-content">
        <div className="EventFiltersCard-form">
          <div className="EventFiltersCard-filtersContainer">
            {Object.keys(currentEventFilters.faults).map((faultCode) => {
              const fault = currentEventFilters.faults[faultCode];
              const hours = fault.filter_threshold_in_seconds / 3600;

              return (
                <div key={fault.fault_code} className="EventFiltersCard-filterRow">
                  <ToggleSwitch
                    isActive={fault.enabled}
                    onToggle={(enabled) => handleFaultFilterChange(fault.fault_code, 'enabled', enabled)}
                    className="EventFiltersCard-enableToggle"
                  />
                  <span className="EventFiltersCard-faultName">{safeGetFaultName(fault.fault_code)}</span>
                  <NumberInput
                    value={Math.abs(hours.toFixed(1))}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      const roundedValue = Math.round(value * 100) / 100; // Limit to 2 decimal places
                      handleFaultFilterChange(fault.fault_code, 'filter_threshold_in_seconds', roundedValue * 3600);
                    }}
                    min={0}
                    max={999}
                    step={0.5}
                    disabled={!fault.enabled}
                    autoSelect={true}
                    className="EventFiltersCard-hoursInput"
                  />
                  <span className="EventFiltersCard-hoursLabel">hours</span>
                  <Select
                    value={fault.filter_type}
                    onChange={(e) => handleFaultFilterChange(fault.fault_code, 'filter_type', e.target.value)}
                    itemList={timeTypeOptions}
                    disabled={!fault.enabled}
                    className="EventFiltersCard-timeTypeSelect"
                  />
                </div>
              );
            })}
          </div>

          {rootCauseList.length > 0 && (
            <div className="EventFiltersCard-rootCauseSection">
              <div className="EventFiltersCard-rootCauseHeader">
                <h3 className="EventFiltersCard-rootCauseTitle">Root Causes to Show</h3>
              </div>
              <div className="EventFiltersCard-rootCauseCheckboxes">
                <Checkbox
                  id="root-cause-unknown"
                  label="Not Specified"
                  checked={currentEventFilters.show_unknown_root_causes}
                  onChange={() => {
                    onEventFiltersChange({
                      ...currentEventFilters,
                      show_unknown_root_causes: !currentEventFilters.show_unknown_root_causes,
                    });
                  }}
                  className="EventFiltersCard-rootCauseCheckbox"
                />
                {rootCauseList.map((rootCause) => (
                  <div key={rootCause.id || rootCause.name} className="EventFiltersCard-rootCauseItem">
                    <Checkbox
                      id={`root-cause-${rootCause.id || rootCause.name}`}
                      label={rootCause.name}
                      checked={currentEventFilters.selected_root_causes.some(
                        (selected) => selected.id === rootCause.id || selected.name === rootCause.name,
                      )}
                      onChange={() => handleRootCauseFilterChange(rootCause)}
                      className="EventFiltersCard-rootCauseCheckbox"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="EventFiltersCard-actions">
            <Button type="button" content="Reset to Defaults" onClick={handleReset} />
            <div className="EventFiltersCard-primaryActions">
              <Button type="button" content="Close" variant="vivid" color="success" onClick={handleClose} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

EventFiltersCard.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  onClose: PropTypes.func,
  currentEventFilters: PropTypes.shape({
    selected_root_causes: PropTypes.array,
    show_unknown_root_causes: PropTypes.bool,
    faults: PropTypes.shape({
      fault_code: PropTypes.number,
      filter_threshold_in_seconds: PropTypes.number,
      filter_type: PropTypes.string,
      enabled: PropTypes.bool,
    }),
  }),
  loading: PropTypes.bool,
  onReset: PropTypes.func,
  onEventFiltersChange: PropTypes.func,
  rootCauseList: PropTypes.array,
};

export default EventFiltersCard;
